import { Button, Input } from '@/components/ui';
import { TextArea } from '@/components/ui/Textarea';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';

const createGroupSchema = (t: any) =>
  z.object({
    name: z.string().min(1, t('groups.nameRequired')),
    description: z.string().optional(),
  });

const updateGroupSchema = (t: any) =>
  z.object({
    name: z.string().min(1, t('groups.nameRequired')),
    description: z.string().optional(),
  });

export type CreateGroupFormData = z.infer<ReturnType<typeof createGroupSchema>>;
export type UpdateGroupFormData = z.infer<ReturnType<typeof updateGroupSchema>>;

export interface CreateGroupFormProps {
  mode: 'create';
  onSubmit: (data: CreateGroupFormData) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

export interface UpdateGroupFormProps {
  mode: 'update';
  onSubmit: (data: UpdateGroupFormData) => Promise<void>;
  isLoading: boolean;
  initialData: {
    name: string;
    description?: string;
  };
  onCancel: () => void;
}

export type GroupFormProps = CreateGroupFormProps | UpdateGroupFormProps;

export function GroupForm(props: GroupFormProps) {
  const { mode, onSubmit, isLoading, onCancel } = props;
  const initialData = props.mode === 'update' ? props.initialData : undefined;

  const { t } = useTranslation();

  const isCreate = mode === 'create';
  const schema = isCreate ? createGroupSchema(t) : updateGroupSchema(t);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: isCreate
      ? {
          name: '',
          description: '',
        }
      : {
          name: initialData?.name || '',
          description: initialData?.description || '',
        },
  });

  useEffect(() => {
    if (initialData && !isCreate) {
      reset({
        name: initialData.name,
        description: initialData.description || '',
      });
    }
  }, [initialData, isCreate, reset]);

  const handleFormSubmit = async (data: any) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="gap-md flex flex-col">
      <Input
        {...register('name')}
        label={t('groups.form.name')}
        placeholder={t('groups.form.namePlaceholder')}
        error={errors.name?.message}
        disabled={isLoading}
      />

      <TextArea
        {...register('description')}
        label={t('groups.form.description')}
        placeholder={t('groups.form.descriptionPlaceholder')}
        error={errors.description?.message}
        disabled={isLoading}
      />

      <footer className="gap-sm mt-2xl flex self-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {t(isCreate ? 'common.create' : 'common.save')}
        </Button>
      </footer>
    </form>
  );
}
